import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  supervisedContractorApi,
  SupervisedContractor,
  SupervisedContractorStats,
  SupervisedContractorFilters,
} from 'shared/services/supervisedContractorApi';
import { contractorApi } from 'shared/services/contractorApi';
import { queryKeys } from '../queryConfig';

/**
 * Filter parameters for supervised contractors list query
 */
export interface SupervisedContractorsFilters {
  search?: string;
  status?: string;
  companyId?: string;
  expiringDocs?: boolean;
  page?: number;
  limit?: number;
}

/**
 * Transformed response from the supervised contractors API
 */
interface SupervisedContractorsData {
  contractors: SupervisedContractor[];
  stats: SupervisedContractorStats | null;
  total: number;
}

/**
 * Hook to fetch supervised contractors with filters and pagination
 *
 * Features:
 * - Automatic caching (5 min TTL)
 * - Request deduplication
 * - Previous data shown while fetching new data (keepPreviousData)
 *
 * @param filters - Filter parameters for the query
 * @param enabled - Whether the query should run
 */
export const useSupervisedContractors = (
  filters: SupervisedContractorsFilters = {},
  enabled = true,
) => {
  const queryParams: Record<string, unknown> = {
    page: (filters.page ?? 0) + 1,
    limit: filters.limit || 10,
    ...(filters.search && { search: filters.search }),
    ...(filters.status && filters.status !== 'all' && { status: filters.status }),
    ...(filters.companyId && filters.companyId !== 'all' && { companyId: filters.companyId }),
    ...(filters.expiringDocs && { expiringDocs: true }),
  };

  return useQuery<SupervisedContractorsData, Error>({
    queryKey: queryKeys.supervisedContractors.list(queryParams, filters.companyId || ''),
    queryFn: async () => {
      const apiFilters: SupervisedContractorFilters = {
        page: queryParams.page as number,
        limit: queryParams.limit as number,
        ...(filters.search && { search: filters.search }),
        ...(filters.status && filters.status !== 'all' && { status: filters.status as 'activo' | 'inactivo' | 'baja' }),
        ...(filters.companyId && filters.companyId !== 'all' && { companyId: filters.companyId }),
        ...(filters.expiringDocs && { expiringDocs: true }),
      };

      const response = await supervisedContractorApi.getAll(apiFilters);

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Error al cargar contratistas');
      }

      const contractorsData = response.data.contractors || [];

      let stats: SupervisedContractorStats | null = null;
      if (response.data.summary) {
        const summary = response.data.summary;
        stats = {
          total: summary.totalContractors,
          active: summary.activeContractors,
          inactive: summary.inactiveContractors,
          suspended: 0,
          expiringDocuments: summary.expiringDocuments,
          companiesCount: summary.companiesSupervised,
          byCompany: summary.contractorsByCompany.map(company => ({
            _id: company.companyId,
            name: company.companyName,
            count: company.totalContractors,
            active: company.activeContractors,
            inactive: company.totalContractors - company.activeContractors,
          })),
        };
      }

      return {
        contractors: contractorsData,
        stats,
        total: response.data.pagination?.total || contractorsData.length,
      };
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
    retry: 1,
  });
};

/**
 * Hook to delete a supervised contractor
 *
 * On success, invalidates supervised contractor queries to refetch fresh data.
 */
export const useDeleteSupervisedContractor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => contractorApi.delete(id, 'Eliminado por supervisor'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.supervisedContractors.all });
    },
  });
};
